import { computed, signal } from '@lit-labs/signals';
import { resolveBackendUrl } from '../utils/backend-url.js';
const locationsSignal = signal([]);
const bucketsSignal = signal([]);
const tagsSignal = signal([]);
const partsSignal = signal([]);
const searchTextSignal = signal('');
const selectedTagsSignal = signal([]);
const onlyMyBucketsSignal = signal(false);
const selectedLocationIdSignal = signal('');
const selectedBucketIdSignal = signal('');
const activeBucketIdSignal = signal('');
const loadingSignal = signal(false);
const savingSignal = signal(false);
const errorSignal = signal(null);
export const bucketLocatorState = computed(() => ({
    locations: locationsSignal.get(),
    buckets: bucketsSignal.get(),
    tags: tagsSignal.get(),
    parts: partsSignal.get(),
    searchText: searchTextSignal.get(),
    selectedTags: selectedTagsSignal.get(),
    onlyMyBuckets: onlyMyBucketsSignal.get(),
    selectedLocationId: selectedLocationIdSignal.get(),
    selectedBucketId: selectedBucketIdSignal.get(),
    activeBucketId: activeBucketIdSignal.get(),
    loading: loadingSignal.get(),
    saving: savingSignal.get(),
    error: errorSignal.get(),
}));
export async function loadBucketLocator() {
    loadingSignal.set(true);
    errorSignal.set(null);
    try {
        const [locations, buckets, tags] = await Promise.all([
            api('/locations'),
            api('/buckets'),
            api('/tags'),
        ]);
        locationsSignal.set(locations);
        bucketsSignal.set(buckets);
        tagsSignal.set(tags);
        const storedBucket = sessionStorage.getItem('df-bucket-locator-active-bucket');
        activeBucketIdSignal.set(storedBucket && buckets.some((bucket) => bucket.id === storedBucket)
            ? storedBucket
            : '');
        await refreshParts();
    }
    catch (error) {
        errorSignal.set(toMessage(error));
    }
    finally {
        loadingSignal.set(false);
    }
}
export async function refreshParts() {
    const params = new URLSearchParams();
    const query = searchTextSignal.get().trim();
    if (query)
        params.set('q', query);
    const bucketId = selectedBucketIdSignal.get();
    if (bucketId)
        params.set('bucket_id', bucketId);
    const locationId = selectedLocationIdSignal.get();
    if (locationId)
        params.set('location_id', locationId);
    for (const tag of selectedTagsSignal.get())
        params.append('tag', tag);
    if (onlyMyBucketsSignal.get())
        params.set('my_buckets', 'true');
    const suffix = params.size ? `?${params.toString()}` : '';
    try {
        partsSignal.set(await api(`/parts${suffix}`));
        errorSignal.set(null);
    }
    catch (error) {
        errorSignal.set(toMessage(error));
    }
}
export function setSearchText(value) {
    searchTextSignal.set(value);
}
export function setSelectedLocationId(value) {
    selectedLocationIdSignal.set(value);
    selectedBucketIdSignal.set('');
}
export function setSelectedBucketId(value) {
    selectedBucketIdSignal.set(value);
}
export function setOnlyMyBuckets(value) {
    onlyMyBucketsSignal.set(value);
}
export function setActiveBucketId(value) {
    activeBucketIdSignal.set(value);
    if (value)
        sessionStorage.setItem('df-bucket-locator-active-bucket', value);
    else
        sessionStorage.removeItem('df-bucket-locator-active-bucket');
}
export function toggleSelectedTag(tagId) {
    const current = selectedTagsSignal.get();
    selectedTagsSignal.set(current.includes(tagId)
        ? current.filter((tag) => tag !== tagId)
        : [...current, tagId]);
}
export async function createLocation(name, description) {
    await withSaving(async () => {
        await api('/locations', {
            method: 'POST',
            body: JSON.stringify({ name, description }),
        });
        locationsSignal.set(await api('/locations'));
    });
}
export async function updateLocation(input) {
    await withSaving(async () => {
        await api(`/locations/${encodeURIComponent(input.id)}`, {
            method: 'PATCH',
            body: JSON.stringify({ name: input.name.trim(), description: input.description.trim() }),
        });
        await refreshAfterMutation();
    });
}
export async function deleteLocation(locationId) {
    await withSaving(async () => {
        await api(`/locations/${encodeURIComponent(locationId)}`, { method: 'DELETE' });
        await refreshAfterMutation();
    });
}
export async function createBucket(locationId, description) {
    return withSaving(async () => {
        const result = await api('/buckets', {
            method: 'POST',
            body: JSON.stringify({ location_id: locationId, description }),
        });
        setActiveBucketId(result.bucket_id);
        await refreshManagementData();
        return result;
    });
}
export async function moveBucket(bucketId, locationId) {
    await withSaving(async () => {
        await api(`/buckets/${encodeURIComponent(bucketId)}/location`, {
            method: 'PATCH',
            body: JSON.stringify({ location_id: locationId }),
        });
        await refreshManagementData();
        await refreshParts();
    });
}
export async function updateBucket(input) {
    await withSaving(async () => {
        await api(`/buckets/${encodeURIComponent(input.id)}`, {
            method: 'PATCH',
            body: JSON.stringify({ location_id: input.locationId, description: input.description.trim() }),
        });
        await refreshAfterMutation();
    });
}
export async function deleteBucket(bucketId) {
    await withSaving(async () => {
        await api(`/buckets/${encodeURIComponent(bucketId)}`, { method: 'DELETE' });
        if (activeBucketIdSignal.get() === bucketId)
            setActiveBucketId('');
        await refreshAfterMutation();
    });
}
export async function createTag(label) {
    await withSaving(async () => {
        await api('/tags', { method: 'POST', body: JSON.stringify({ label: label.trim() }) });
        await refreshManagementData();
    });
}
export async function updateTag(tagId, label) {
    await withSaving(async () => {
        await api(`/tags/${encodeURIComponent(tagId)}`, {
            method: 'PATCH', body: JSON.stringify({ label: label.trim() }),
        });
        await refreshManagementData();
        await refreshParts();
    });
}
export async function deleteTag(tagId) {
    await withSaving(async () => {
        await api(`/tags/${encodeURIComponent(tagId)}`, { method: 'DELETE' });
        if (selectedTagsSignal.get().includes(tagId))
            toggleSelectedTag(tagId);
        await refreshManagementData();
        await refreshParts();
    });
}
export async function intakePart(input) {
    await withSaving(async () => {
        const form = new FormData();
        form.set('file', input.file, 'part.jpg');
        form.set('bucket_id', input.bucketId);
        form.set('name', input.name.trim());
        form.set('description', input.description.trim());
        form.set('quantity', String(input.quantity));
        form.set('tags', JSON.stringify(input.tags));
        await api('/parts/intake', {
            method: 'POST',
            body: form,
        });
        setActiveBucketId(input.bucketId);
        await refreshManagementData();
        await refreshParts();
    });
}
export async function decrementPart(partId, count = 1) {
    return withSaving(async () => {
        const result = await api(`/parts/${encodeURIComponent(partId)}/decrement`, { method: 'POST', body: JSON.stringify({ count }) });
        await refreshManagementData();
        await refreshParts();
        return result;
    });
}
export async function updatePart(input) {
    return withSaving(async () => {
        const result = await api(`/parts/${encodeURIComponent(input.id)}`, {
            method: 'PATCH',
            body: JSON.stringify({
                name: input.name.trim(),
                description: input.description.trim(),
                quantity: input.quantity,
                bucket_id: input.bucketId,
                tags: input.tags,
            }),
        });
        await refreshManagementData();
        await refreshParts();
        return result;
    });
}
export async function deletePart(partId) {
    await withSaving(async () => {
        await api(`/parts/${encodeURIComponent(partId)}`, { method: 'DELETE' });
        await refreshAfterMutation();
    });
}
async function refreshAfterMutation() {
    await refreshManagementData();
    await refreshParts();
}
async function refreshManagementData() {
    const [locations, buckets, tags] = await Promise.all([
        api('/locations'),
        api('/buckets'),
        api('/tags'),
    ]);
    locationsSignal.set(locations);
    bucketsSignal.set(buckets);
    tagsSignal.set(tags);
}
async function withSaving(operation) {
    savingSignal.set(true);
    errorSignal.set(null);
    try {
        return await operation();
    }
    catch (error) {
        errorSignal.set(toMessage(error));
        throw error;
    }
    finally {
        savingSignal.set(false);
    }
}
async function api(path, init = {}) {
    const response = await fetch(resolveBackendUrl(`/api${path}`), {
        ...init,
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            ...(init.body instanceof FormData
                ? {}
                : { 'Content-Type': 'application/json' }),
            ...init.headers,
        },
    });
    if (!response.ok) {
        const payload = (await response.json().catch(() => null));
        throw new Error(payload?.error ?? `Request failed (${response.status})`);
    }
    return (response.status === 204 ? undefined : response.json());
}
function toMessage(error) {
    return error instanceof Error ? error.message : 'The request failed';
}
//# sourceMappingURL=bucket-locator.store.js.map